import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@iconify/react';
import { useAuth } from '../../contexts/AuthContext';
import { backendProvider } from '../../services/backend';
import { teamInviteService } from '../../services/backend/appwrite/teamInviteService';
import { GeoSelect } from '../atoms/GeoSelect';
import { MemberAvatar } from './atoms/AdminPrimitives';
import { isTeamAdmin } from './teamMemberGroups';
import { MemberActivity } from './atoms/MemberActivity';
import { TeamDirectory } from './atoms/TeamDirectory';
import { PICKER_LOCALE_OPTIONS } from '../../constants/locales';
import { queryKeys } from '../../services/query/queryKeys';
import type { TeamInvitationData, TeamMemberData } from '../../types/n8nResponses';
import { getTeamAccessStatus, teamAccessTone } from './teamAccess';

interface InviteTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamMembers: TeamMemberData[];
  teamInvitations: TeamInvitationData[];
  embedded?: boolean;
}

const formatInviteDate = (value?: string | null) => {
  if (!value) return 'Unknown date';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown date';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
};

export const InviteTeamMemberModal: React.FC<InviteTeamMemberModalProps> = ({
  isOpen,
  onClose,
  teamMembers,
  teamInvitations,
  embedded = false,
}) => {
  const { currentUser, targetEmail } = useAuth();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [success, setSuccess] = useState(false);
  const [deliveryWarning, setDeliveryWarning] = useState<string | null>(null);
  const [optimisticInvitations, setOptimisticInvitations] = useState<TeamInvitationData[]>([]);
  const [optimisticRemovedMembers, setOptimisticRemovedMembers] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [accessFilter, setAccessFilter] = useState('all');

  const assertActionSuccess = <T,>(response: { success: boolean; error?: string; data?: T }) => {
    if (!response.success) {
      throw new Error(response.error || 'Request failed');
    }

    return response;
  };

  const normalizedTargetEmail = targetEmail?.trim().toLowerCase() || null;

  useEffect(() => {
    setOptimisticInvitations((previous) =>
      previous.filter((optimisticInvite) => {
        return !teamInvitations.some((serverInvite) => {
          const sameEmail =
            serverInvite.email.trim().toLowerCase() === optimisticInvite.email.trim().toLowerCase();
          const sameStatus = serverInvite.status === optimisticInvite.status;
          return sameEmail && sameStatus;
        });
      })
    );
  }, [teamInvitations]);

  const mergedInvitations = useMemo(() => {
    return [...teamInvitations, ...optimisticInvitations];
  }, [teamInvitations, optimisticInvitations]);

  const rosterMembers = useMemo(() => {
    const acceptedInvitations = mergedInvitations.filter(
      (invite) => invite.status === 'accepted' && (invite.user_id || invite.email)
    );

    const seen = new Set<string>();
    return [...teamMembers, ...acceptedInvitations].filter((member) => {
      const memberKey = member.user_id || member.email.trim().toLowerCase();
      if (seen.has(memberKey)) return false;
      seen.add(memberKey);
      return true;
    });
  }, [teamMembers, mergedInvitations]);

  const impersonatedOwner = useMemo(() => {
    if (!normalizedTargetEmail) return null;

    return (
      rosterMembers.find((member) => member.email.trim().toLowerCase() === normalizedTargetEmail) || null
    );
  }, [normalizedTargetEmail, rosterMembers]);

  const effectiveUserEmail = targetEmail || currentUser?.email || '';
  const effectiveAdminEmail = impersonatedOwner?.email || effectiveUserEmail;
  const effectiveAdminUserId = impersonatedOwner?.user_id || currentUser?.uid || '';
  const canResolveTeamServerSide = Boolean(effectiveAdminUserId || effectiveAdminEmail);
  const teamId =
    impersonatedOwner?.team_id ??
    rosterMembers[0]?.team_id ??
    teamInvitations[0]?.team_id ??
    '';

  const isProtectedLeader = (member: Pick<TeamMemberData | TeamInvitationData, 'user_id' | 'email' | 'roles'>) => {
    const normalizedMemberEmail = member.email.trim().toLowerCase();
    const normalizedAdminEmail = effectiveAdminEmail.trim().toLowerCase();

    return (
      member.user_id === effectiveAdminUserId ||
      normalizedMemberEmail === normalizedAdminEmail ||
      Boolean(member.roles?.includes('owner'))
    );
  };

  const visibleTeamMembers = useMemo(() => {
    return rosterMembers
      .filter((member) => !optimisticRemovedMembers.includes(member.user_id || member.email))
      .sort((left, right) => {
        const leftIsCurrentUser = isProtectedLeader(left);
        const rightIsCurrentUser = isProtectedLeader(right);

        if (leftIsCurrentUser && !rightIsCurrentUser) return -1;
        if (!leftIsCurrentUser && rightIsCurrentUser) return 1;

        return (left.name || left.email).localeCompare(right.name || right.email);
      });
  }, [rosterMembers, optimisticRemovedMembers, effectiveAdminEmail, effectiveAdminUserId]);

  const sortedInvitations = useMemo(() => {
    return [...mergedInvitations].sort((left, right) => {
      if (left.status !== right.status) {
        return left.status === 'pending' ? -1 : 1;
      }

      const leftDate = new Date(left.invited_at || left.joined_at || 0).getTime();
      const rightDate = new Date(right.invited_at || right.joined_at || 0).getTime();
      return rightDate - leftDate;
    });
  }, [mergedInvitations]);


  useEffect(() => {
    if (!isOpen || embedded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen, embedded]);

  useEffect(() => {
    if (!isOpen) return;
  }, [isOpen]);

  const mutation = useMutation({
    onMutate: () => {
      setSuccess(false);
      setDeliveryWarning(null);
    },
    mutationFn: async () =>
      teamInviteService.sendInvite({
        invite_email: email.trim(),
        invite_name: name.trim() || undefined,
        team_id: teamId,
        admin_user_id: effectiveAdminUserId,
        admin_email: effectiveAdminEmail,
      }),
    onSuccess: (result) => {
      const invitedEmail = email.trim();
      const joinedAt = new Date().toISOString();
      const optimisticInvite: TeamInvitationData = {
        membership_id: result.data?.membership_id || `optimistic-${invitedEmail.toLowerCase()}-${Date.now()}`,
        user_id: result.data?.user_id || null,
        email: invitedEmail,
        name: name.trim() || invitedEmail,
        team_id: result.data?.team_id || teamId || undefined,
        status: result.data?.status || 'pending',
        invited_at: joinedAt,
        joined_at: result.data?.status === 'accepted' ? joinedAt : null,
      };

      setOptimisticInvitations((previous) => {
        const alreadyTracked = previous.some(
          (invite) => invite.email.trim().toLowerCase() === optimisticInvite.email.trim().toLowerCase()
        );
        return alreadyTracked ? previous : [optimisticInvite, ...previous];
      });

      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.list(effectiveUserEmail) });
      queryClient.invalidateQueries({ queryKey: ['team-roster', effectiveUserEmail] });
      queryClient.refetchQueries({ queryKey: queryKeys.campaigns.list(effectiveUserEmail), type: 'active' });
      setSuccess(true);
      setEmail('');
      setName('');

      backendProvider.auth.sendMagicLink(invitedEmail).catch((error: any) => {
        console.error('Failed to send invite magic link email:', error);
        setDeliveryWarning(error?.message || 'The team invitation was created, but the magic link email could not be delivered.');
      });
    },
    onError: (error: any) => {
      setDeliveryWarning(error?.message || null);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ membershipId, invitationTeamId }: { membershipId: string; invitationTeamId: string }) =>
      teamInviteService.cancelInvite({
        membership_id: membershipId,
        team_id: invitationTeamId,
      }),
    onSuccess: () => {
      setOptimisticInvitations((previous) =>
        previous.filter((invite) => invite.membership_id !== cancelMutation.variables?.membershipId)
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.list(effectiveUserEmail) });
      queryClient.invalidateQueries({ queryKey: ['team-roster', effectiveUserEmail] });
      queryClient.refetchQueries({ queryKey: queryKeys.campaigns.list(effectiveUserEmail), type: 'active' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async ({ membershipId, invitationTeamId, userId, userEmail }: { membershipId: string; invitationTeamId: string; userId?: string | null; userEmail: string }) =>
      teamInviteService.removeMember({
        membership_id: membershipId,
        team_id: invitationTeamId,
        remove_user_id: userId || '',
        remove_user_email: userEmail,
        admin_user_id: effectiveAdminUserId,
        admin_email: effectiveAdminEmail,
      }),
    onSuccess: () => {
      const removedKey = removeMutation.variables?.userId || removeMutation.variables?.userEmail;
      if (removedKey) {
        setOptimisticRemovedMembers((previous) =>
          previous.includes(removedKey) ? previous : [...previous, removedKey]
        );
      }
      setOptimisticInvitations((previous) =>
        previous.filter((invite) => invite.membership_id !== removeMutation.variables?.membershipId)
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.list(effectiveUserEmail) });
      queryClient.invalidateQueries({ queryKey: ['team-roster', effectiveUserEmail] });
      queryClient.refetchQueries({ queryKey: queryKeys.campaigns.list(effectiveUserEmail), type: 'active' });
    },
  });

  /** Persist a status/label/language change for an accepted member via update_member. */
  const memberUpdate = useMutation({
    mutationFn: (vars: { userId: string; status?: 'active' | 'inactive' | 'blocked'; add_labels?: string[]; remove_labels?: string[]; language?: string | null }) =>
      teamInviteService.updateMember({
        user_id: vars.userId,
        status: vars.status,
        add_labels: vars.add_labels,
        remove_labels: vars.remove_labels,
        language: vars.language,
        admin_user_id: effectiveAdminUserId,
        admin_email: effectiveAdminEmail,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-roster', effectiveUserEmail] });
    },
  });

  const handleClose = () => {
    setSuccess(false);
    setDeliveryWarning(null);
    setEmail('');
    setName('');
    setOptimisticInvitations([]);
    setOptimisticRemovedMembers([]);
    mutation.reset();
    cancelMutation.reset();
    removeMutation.reset();
    onClose();
  };

  if (!isOpen || typeof document === 'undefined') return null;

  if (embedded) {
    const allRows = [...visibleTeamMembers, ...sortedInvitations.filter((invite) => invite.status === 'pending')];
    const rows = allRows
      .filter((member) => `${member.name} ${member.email}`.toLowerCase().includes(memberSearch.trim().toLowerCase()))
      .filter((member) => accessFilter === 'all' || getTeamAccessStatus(member).toLowerCase() === accessFilter);

    const formatLastLogin = (value?: string | null) => {
      if (!value) return 'Unavailable';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return '—';
      // Date + time — the user asked for the time alongside the day.
      return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(parsed);
    };
    const formatSignup = (value?: string | null) => {
      if (!value) return 'Unavailable';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return '—';
      return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(parsed);
    };
    const isTeamAdminLabel = isTeamAdmin;
    const memberBusy = (userId?: string | null) =>
      memberUpdate.isPending && memberUpdate.variables?.userId === userId;

    return (
      <div className="team-members-embedded flex flex-col gap-2">
        {(removeMutation.isError || cancelMutation.isError || memberUpdate.isError) && <p role="alert" className="p-3 text-sm text-rose-300">{removeMutation.error?.message || cancelMutation.error?.message || memberUpdate.error?.message}</p>}
        <TeamDirectory
          listLabel="Team members"
          groupBy={member => isTeamAdmin(member) ? 'Admins' : 'Users'}
          groupOrder={['Admins', 'Users']}
          searchValue={memberSearch}
          onSearchChange={setMemberSearch}
          searchPlaceholder="Search by name or email…"
          toolbarExtras={
            <select aria-label="Filter member status" className="team-directory-select !w-auto" value={accessFilter} onChange={(event) => setAccessFilter(event.target.value)}>
              <option value="all">All statuses</option><option value="active">Active</option><option value="invited">Invited</option><option value="joined">Joined</option><option value="inactive">Inactive</option><option value="blocked">Blocked</option>
            </select>
          }
          resultCount={`${rows.length} of ${allRows.length} people`}
          columnsVariant="wide"
          columns={[
            {
              key: 'status',
              label: 'Status',
              render: (member) => {
                const pending = member.status === 'pending';
                const protectedMember = isProtectedLeader(member);
                const status = getTeamAccessStatus(member);
                const statusAsPref = (member.access_status || '').toLowerCase();
                const setStatus = (next: 'active' | 'inactive' | 'blocked') => {
                  if (!member.user_id) return;
                  memberUpdate.mutate({ userId: member.user_id, status: next });
                };
                return pending ? (
                  <span data-status={status.toLowerCase()} className="team-member-status team-directory-status">{status}</span>
                ) : (
                  <select
                    aria-label={`Access status for ${member.name || member.email}`}
                    className="team-directory-select team-directory-status"
                    data-status={status.toLowerCase()}
                    value={['active', 'inactive', 'blocked'].includes(statusAsPref) ? statusAsPref : status.toLowerCase()}
                    disabled={protectedMember || !member.user_id || memberBusy(member.user_id)}
                    onChange={(event) => setStatus(event.target.value as 'active' | 'inactive' | 'blocked')}>
                    {!['active', 'inactive', 'blocked'].includes(statusAsPref) && <option value={status.toLowerCase()} disabled>{status}</option>}
                    <option value="active">Active</option><option value="inactive">Inactive</option><option value="blocked">Blocked</option>
                  </select>
                );
              },
            },
            {
              key: 'language',
              label: 'Language',
              render: (member) => {
                const pending = member.status === 'pending';
                return (
                  <select aria-label={`Language for ${member.name || member.email}`} className="team-directory-select"
                    value={('language' in member && member.language) || ''}
                    disabled={pending || !member.user_id || memberBusy(member.user_id)}
                    onChange={(event) => memberUpdate.mutate({ userId: member.user_id!, language: event.target.value || null })}>
                    <option value="">Not set</option>
                    {'language' in member && member.language && !PICKER_LOCALE_OPTIONS.some((option) => option.code === member.language) && <option value={member.language}>{member.language}</option>}
                    {PICKER_LOCALE_OPTIONS.map((option) => <option key={option.code} value={option.code}>{option.label}</option>)}
                  </select>
                );
              },
            },
            {
              key: 'tag',
              label: 'Tag',
              render: (member) => {
                const pending = member.status === 'pending';
                const protectedMember = isProtectedLeader(member);
                const adminLabelChecked = isTeamAdminLabel(member);
                const toggleAdminLabel = () => {
                  if (!member.user_id) return;
                  memberUpdate.mutate({
                    userId: member.user_id,
                    add_labels: adminLabelChecked ? undefined : ['teamadmin'],
                    remove_labels: adminLabelChecked ? ['teamadmin'] : undefined,
                  });
                };
                return pending ? (
                  <span className="team-directory-pending">Pending</span>
                ) : (
                  <select
                    aria-label={`Tag for ${member.name || member.email}`}
                    className={`team-directory-select ${adminLabelChecked ? 'team-directory-tag-admin' : ''}`}
                    value={adminLabelChecked ? 'teamadmin' : 'member'}
                    disabled={protectedMember || !member.user_id || memberBusy(member.user_id)}
                    onChange={toggleAdminLabel}>
                    <option value="member">Member</option><option value="teamadmin">TeamAdmin</option>
                  </select>
                );
              },
            },
            {
              key: 'signup',
              label: 'Signed up',
              narrowHidden: true,
              render: (member) => {
                const joined = member.registered_at || null;
                return (
                  <span className="team-directory-login">
                    {member.status === 'pending' ? 'Invited, not joined' : formatSignup(joined)}
                  </span>
                );
              },
            },
            {
              key: 'activity',
              label: 'Activity (15d)',
              render: (member) => <MemberActivity activity={member.activity_events} status={member.activity_status} pending={member.status === 'pending'} />,
            },
            {
              key: 'last-login',
              label: 'Last access',
              narrowHidden: true,
              render: (member) => (
                <span className="team-directory-login" title="Appwrite last access timestamp; may update at most once every 24 hours.">
                  {member.status === 'pending' ? 'Not joined yet' : formatLastLogin(member.last_login)}
                </span>
              ),
            },
          ]}
          rows={rows}
          rowKey={(member) => member.membership_id || member.user_id || member.email}
          identity={(member) => ({
            avatar: (
              <MemberAvatar
                name={member.name}
                email={member.email}
                imageUrl={member.profile_picture_url}
                className="team-avatar"
              />
            ),
            title: (
              <>
                {member.name || member.email}
                {member.user_id === currentUser?.uid && <span className="team-directory-you">You</span>}
              </>
            ),
            subtitle: <span title={member.email}>{member.email}</span>,
          })}
          action={(member) => {
            const pending = member.status === 'pending';
            const protectedMember = isProtectedLeader(member);
            const busy = pending ? cancelMutation.isPending && cancelMutation.variables?.membershipId === member.membership_id
              : removeMutation.isPending && removeMutation.variables?.membershipId === member.membership_id;
            const removable = !protectedMember && member.team_id && member.membership_id;
            if (!removable) return { hide: true };
            return {
              content: (
                <button type="button" className="team-action" disabled={busy} onClick={() => pending
                  ? cancelMutation.mutate({ membershipId: member.membership_id!, invitationTeamId: member.team_id! })
                  : removeMutation.mutate({ membershipId: member.membership_id!, invitationTeamId: member.team_id!, userId: member.user_id, userEmail: member.email })}>
                  {busy ? 'Saving…' : pending ? 'Cancel invite' : 'Remove'}
                </button>
              ),
            };
          }}
          emptyState={{
            icon: 'solar:users-group-rounded-linear',
            title: allRows.length ? 'No matching members' : 'Your team starts here',
            description: allRows.length ? 'Try another name, email or access status.' : 'Invite a colleague to start working together.',
          }}
          emptyAction={allRows.length > 0 ? (
            <button type="button" className="team-action" onClick={() => { setMemberSearch(''); setAccessFilter('all'); }}>Clear filters</button>
          ) : undefined}
        />
      </div>
    );
  }

  const content = (
    <div
      className={embedded ? 'team-members-embedded' : 'fixed inset-0 z-[10030] flex items-center justify-center p-4'}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      {!embedded && <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.12),transparent_28%),rgba(2,6,12,0.88)] backdrop-blur-md" />}
      <div className={embedded ? '' : 'relative z-10 w-full max-w-5xl overflow-hidden rounded-[34px] border border-white/10 bg-[#101012] shadow-[0_34px_100px_rgba(0,0,0,0.5)]'}>
        <div className={embedded ? '' : 'grid min-h-[560px] gap-0 lg:grid-cols-[0.82fr_1.18fr]'}>
          <div className={embedded ? 'hidden' : 'relative hidden overflow-hidden border-r border-white/8 bg-[#0A101C] p-8 lg:flex lg:flex-col lg:justify-between'}>
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute -left-10 top-10 h-36 w-36 rounded-full bg-cyan-300/15 blur-3xl" />
              <div className="absolute bottom-0 right-0 h-56 w-56 rounded-full bg-[#5260FF]/18 blur-3xl" />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),transparent_28%,rgba(5,8,14,0.28)_100%)]" />
            </div>

            <div className="relative z-10 max-w-sm">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/18 bg-cyan-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.24em] text-cyan-100/90">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" />
                Team Access
              </div>
              <h2 className="mt-5 text-[2.35rem] font-black leading-[1.02] tracking-[-0.03em] text-white">
                Bring the next teammate into the campaign flow.
              </h2>
              <p className="mt-5 max-w-[18rem] text-sm leading-6 text-slate-200">
                Send access in seconds so they can pick up campaigns, review ownership, and keep the team moving.
              </p>
            </div>

            <div className="relative z-10 grid gap-3">
              {[
                { icon: 'solar:mailbox-bold', label: 'Email invitation', tone: 'text-cyan-100' },
                { icon: 'solar:users-group-rounded-bold', label: 'Joins as team member', tone: 'text-emerald-100' },
                { icon: 'solar:shield-check-bold', label: 'Keeps leader controls protected', tone: 'text-violet-100' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-3 rounded-[22px] border border-white/10 bg-white/[0.05] px-4 py-3 backdrop-blur-sm"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-[linear-gradient(145deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))]">
                    <Icon icon={item.icon} className={`h-5 w-5 ${item.tone}`} aria-hidden="true" />
                  </div>
                  <div className="text-sm font-semibold text-white/88">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative flex flex-col">
            <div className={embedded ? 'hidden' : 'flex items-start justify-between border-b border-white/8 px-6 py-6 sm:px-8'}>
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-white/55 lg:hidden">
                  Invite Member
                </div>
                <h2 className="mt-3 text-xl font-black tracking-tight text-white sm:text-2xl">Invite Member</h2>
                <p className="mt-2 text-sm leading-6 text-[#8E95AE]">Send access, review your live roster, and manage invitations without leaving Team View.</p>
              </div>
              <button
                onClick={handleClose}
                aria-label="Close invitation dialog"
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] text-white/35 transition-all hover:border-white/16 hover:bg-white/[0.06] hover:text-white/80"
              >
                <Icon icon="solar:close-circle-linear" className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className={embedded ? '' : 'max-h-[75vh] flex-1 overflow-y-auto px-6 py-6 sm:px-8 sm:py-7'}>
              <div className="flex h-full flex-col gap-5">
                <form
                  onSubmit={(e) => { e.preventDefault(); if (email.trim()) mutation.mutate(); }}
                  className="flex flex-col gap-5"
                >
                  <div className="rounded-[28px] border border-white/8 bg-[linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-5">
                    <div className="grid gap-4">
                      <div>
                        <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-[#6F7893]">
                          Email *
                        </label>
                        <div className="relative">
                          <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="colleague@company.com"
                            className="h-[52px] w-full rounded-2xl border border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] pl-12 pr-4 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] placeholder:text-[#68758F] transition-all hover:border-cyan-300/24 focus:border-cyan-300/32 focus:outline-none focus:ring-2 focus:ring-cyan-300/10"
                          />
                          <Icon icon="solar:letter-linear" className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#68758F]" aria-hidden="true" />
                        </div>
                      </div>
                      <div>
                        <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-[#6F7893]">
                          Name
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Full name"
                            className="h-[52px] w-full rounded-2xl border border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] pl-12 pr-4 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] placeholder:text-[#68758F] transition-all hover:border-cyan-300/24 focus:border-cyan-300/32 focus:outline-none focus:ring-2 focus:ring-cyan-300/10"
                          />
                          <Icon icon="solar:user-linear" className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#68758F]" aria-hidden="true" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {success && (
                    <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-100">
                      Invitation sent — the teammate was added to the team and a sign-in magic link email is being delivered.
                    </div>
                  )}

                  {deliveryWarning && (
                    <div className="rounded-2xl border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
                      The invitation request reached Appwrite, but email delivery could not be confirmed: {deliveryWarning}
                    </div>
                  )}

                  {mutation.isError && (
                    <div className="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100">
                      {(mutation.error as any)?.message || 'Failed to invite'}
                    </div>
                  )}

                  {cancelMutation.isError && (
                    <div className="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100">
                      {(cancelMutation.error as any)?.message || 'Failed to cancel invitation'}
                    </div>
                  )}

                  {removeMutation.isError && (
                    <div className="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100">
                      {(removeMutation.error as any)?.message || 'Failed to remove member'}
                    </div>
                  )}

                  {!teamId && !canResolveTeamServerSide && (
                    <div className="rounded-2xl border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
                      No team found. Make sure you belong to a team before inviting someone.
                    </div>
                  )}

                  <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="inline-flex h-[46px] items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] px-5 text-[10px] font-black uppercase tracking-[0.18em] text-white/70 transition-all hover:border-white/16 hover:bg-white/[0.05] hover:text-white"
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      disabled={mutation.isPending || !email.trim() || (!teamId && !canResolveTeamServerSide)}
                      className="inline-flex h-[46px] items-center justify-center gap-2 rounded-2xl border border-cyan-300/18 bg-[linear-gradient(135deg,rgba(34,211,238,0.16),rgba(14,22,34,0.92))] px-6 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-100 transition-all hover:-translate-y-0.5 hover:border-cyan-300/34 hover:shadow-[0_16px_34px_rgba(34,211,238,0.14)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                    >
                      {mutation.isPending ? (
                        <>
                          <Icon icon="solar:refresh-bold" className="h-4 w-4 animate-spin" aria-hidden="true" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Icon icon="solar:letter-bold" className="h-4 w-4" aria-hidden="true" />
                          Send Invitation
                        </>
                      )}
                    </button>
                  </div>
                </form>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  return embedded ? content : createPortal(content, document.body);
};
